import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import debounce from 'lodash/debounce';
import { useParams, useRouter } from 'next/navigation';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import { useContextSelector } from 'use-context-selector';

import { fetchMyCognitionThreads } from '@/core/apis/asset-manager-apis';
import { AddUserSharedSpaces, fetchThreadsList, getBusinessThreads } from '@/core/apis/share-space-api';
import { cacheSetAction } from '@/core/store/cache/cache-reducers';
import { cacheSelector } from '@/core/store/cache/cache-state';
import { CacheContext, setCacheState } from '@/core/store/cache/CacheProvider';
import { Authentication } from '@/core/store/global/app-context-types';
import { authSelector } from '@/core/store/global/app-states';
import { AppContext } from '@/core/store/global/AppProvider';
import { appSettings } from '@/settings/app-settings';
import TabsBar002 from '@/ux/pages/ProfilePage/TabsBar002';
import Loader from '@/ux-legacy/components/common/Loader/Loader';
import { Contact } from '@/ux-legacy/components/send/send-types';

const shareSpaceFilter = [
  {
    name: 'Personal',
    value: 'ALL',
  },
  {
    name: 'Business',
    value: 'BUSINESS',
  },
  { name: 'My Cognition', value: 'MY COGNITION' },
  // { name: 'Personals', value: 'PERSONAL' },
  // { name: 'Groups', value: 'GROUP' },
];

const MessageListing: FC = () => {
  const [spaces, setSpaces] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const cache = useContextSelector(CacheContext, cacheSelector);
  const { postId } = useParams();

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showResults, setShowResults] = useState<boolean>(false);
  const limit = 12;
  const [offset, setOffset] = useState(0);
  const cacheDispatch = useContextSelector(CacheContext, setCacheState);
  const [cognitionList, setCognitionList] = useState<any[]>([]);

  const router = useRouter();
  const authentication: Authentication = useContextSelector(AppContext, authSelector);
  // const { count, offset, moreElement, setCurrentCount, setCurrentOffset } = usePagination(loadMore, 0);
  const [showMessage, setShowMessage] = useState(false);
  const [messageUser, setMessageUser] = useState<any[]>([]);
  const [selectedSharedId, setSelectedShareId] = useState('');
  const [selectedFilter, setSelectedFilter] = useState(shareSpaceFilter[0]?.value);

  const moreElement = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cache[`threadToOpen_${postId}`]) {
      const data = cache[`threadToOpen_${postId}`];
      setShowMessage(true);
      setMessageUser(data?.userList);
      setSelectedShareId(data?.message?.sharedSpaceId);
    }
  }, [cache[`threadToOpen_${postId}`]]);

  const fetchSharedSpaces = (limit: number, offset: number, query: string) => {
    fetchThreadsList({ limit, offset, type: 'PERSONAL', query })
      .then((res) => {
        if (!query || query === '') {
          setSpaces((prev) => [...prev, ...(res?.data?.threads || [])]);
          setHasMore(res?.data?.threads.length === limit);
          setShowResults(false);
        } else {
          if (offset === 0) {
            setContacts(res.data.threads || []);
          } else {
            setContacts((prev) => [...prev, ...(res.data.threads || [])]);
          }
          setHasMore(res.data.threads.length === limit);
          setShowResults(true);
        }
      })
      .catch((error) => {
        console.log('error', error);
        setHasMore(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchCognitionThreads = (limit: number, offset: number) => {
    fetchMyCognitionThreads({ limit, offset })
      .then((res) => {
        setCognitionList((prev) => [...prev, ...res.data.threads]);
        setHasMore(res.data.threads.length === limit);
      })
      .catch((error) => {
        console.log('error', error);
        setHasMore(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  //getBusinessThreads
  const fetchBusinessThreads = (limit: number, offset: number) => {
    getBusinessThreads({ limit, offset })
      .then((res) => {
        setSpaces((prev) => [...prev, ...res.data.threads]);
        setHasMore(res.data.threads.length === limit);
      })
      .catch((error) => {
        console.log('error', error);
        setHasMore(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onInterSection = (entries: any) => {
    const firstEntry = entries[0];

    if (firstEntry.isIntersecting && !isLoading) {
      setIsLoading(true);
      setOffset(limit + offset);

      if (selectedFilter === 'BUSINESS') {
        fetchBusinessThreads(limit, offset + limit);
      } else if (selectedFilter === 'MY COGNITION') {
        fetchCognitionThreads(limit, offset + limit);
      } else {
        fetchSharedSpaces(limit, offset + limit, searchQuery);
      }
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(onInterSection, { threshold: 0.5 });

    if (observer && moreElement?.current && !isLoading) {
      observer.observe(moreElement.current);
    }
    return () => {
      if (observer && moreElement?.current) {
        observer.unobserve(moreElement.current);
      }
    };
  }, [spaces?.length, contacts?.length]);

  const fetchAllContacts = (search?: string) => {
    if (search === '') {
      setIsLoading(true);
      setOffset(limit + offset);
      fetchSharedSpaces(limit, offset + limit, '');
      setContacts([]);
      setShowResults(false);
    } else {
      setIsLoading(true);
      setOffset(0);
      fetchSharedSpaces(limit, 0, search || '');
    }
  };

  const debouncedSearchContacts = useCallback(debounce(fetchAllContacts, 250), []);

  // const toggleSelectedContacts = (contact: Contact) => {
  //   if (selectedContacts.find((selectedContact) => selectedContact.destinationId == contact.destinationId)) {
  //     setSelectedContacts(
  //       selectedContacts.filter((selectedContact) => selectedContact.destinationId != contact.destinationId),
  //     );
  //   } else {
  //     setSelectedContacts([...selectedContacts, contact]);
  //   }
  //   setShowResults(false);
  // };

  const onSubmit = () => {
    const userIDs = selectedContacts.map((contact) => contact.destinationId);
    AddUserSharedSpaces(userIDs).then((res) => {
      const id = res.data.thread._id;
      router.push(`/thread/${id}`);
    });
  };

  function generateUserString(users: any[], count: number) {
    let result = '';

    users.slice(0, count).forEach((user, index) => {
      if (index !== 0) {
        result += users.length > index + 1 || users.length > count ? ', ' : ' and ';
      }
      const name = user?.Name || user?.Handle || 'No_Name';

      if (name && users.length > 1) {
        result += name.split(' ')[0];
      } else {
        result = name;
      }
    });

    if (users.length > count) {
      result += ` and ${users.length - count} More.`;
    }

    return result;
  }

  useEffect(() => {
    if (spaces?.length === 0) {
      setIsLoading(true);
      setSpaces([]);
      setCognitionList([]);
      setOffset(0);
      if (selectedFilter === 'BUSINESS') {
        fetchBusinessThreads(limit, 0);
      } else if (selectedFilter === 'MY COGNITION') {
        fetchCognitionThreads(limit, 0);
      } else {
        fetchSharedSpaces(limit, 0, '');
      }
    }
  }, [selectedFilter]);

  const formatRelativeTime = (date: string | number | Date) => {
    const now = new Date();
    const inputDate = new Date(date);
    const secondsDiff = Math.round((now.getTime() - inputDate.getTime()) / 1000);

    if (secondsDiff < 60) {
      return `${secondsDiff} sec`;
    }

    const minutesDiff = Math.round(secondsDiff / 60);
    if (minutesDiff < 60) {
      return `${minutesDiff} min`;
    }

    const hoursDiff = Math.round(minutesDiff / 60);
    if (hoursDiff < 24) {
      return `${hoursDiff} h`;
    }

    const daysDiff = Math.round(hoursDiff / 24);
    if (daysDiff < 30) {
      return `${daysDiff} d`;
    }

    const monthsDiff = Math.round(daysDiff / 30);
    if (monthsDiff < 12) {
      return `${monthsDiff} m`;
    }

    const yearsDiff = Math.round(monthsDiff / 12);
    return `${yearsDiff} y`;
  };

  const formatLastMessage = (lastMessage: string): string => {
    const [name, ...rest] = lastMessage.split(':');
    const message = rest.join(':');

    if (name.trim() === authentication?.profileData?.fullName) {
      return `You:${message}`;
    }

    return lastMessage;
  };

  const spacesCreate = (Id: any, contact: any) => {
    if (contact?.threadName) {
      router.push(`/thread/${contact?._id}`);
    } else {
      AddUserSharedSpaces([Id]).then((res) => {
        const id = res.data.thread._id;
        // if (window.innerWidth < 960) {
        if (contact) {
          const users = {
            Id: contact?.destinationId,
            Name: contact?.name,
            Email: contact?.email,
            Handle: contact?.handle,
            DisplayPic: contact?.displayPic,
          };
          // setShowMessage(true);
          // setMessageUser([users]);
          // setSelectedShareId(id);
          router.push(`/thread/${id}`);
        }
        // } else {
        //   router.push(`/friendship/${id}`);
        // }
      });
    }
  };

  const closeDialog = () => {
    setShowMessage(false);
    setSelectedShareId('');
    setMessageUser([]);
  };

  useEffect(() => {
    if (cache[`shareSpaceToOpen_${authentication?.userId}`]) {
      const data = cache[`shareSpaceToOpen_${authentication?.userId}`];
      const users = data?.sharedSpace?.users.filter((user: any) => user?.Id != authentication?.userId);
      setShowMessage(true);
      setMessageUser(users);
      setSelectedShareId(data?.sharedSpace?._id);
      cacheDispatch(
        cacheSetAction({
          [`shareSpaceToOpen_${authentication?.userId}`]: undefined,
        }),
      );
    }
  }, [cache[`shareSpaceToOpen_${authentication?.userId}`]]);

  return (
    <>
      <div className="w-full flex flex-col px-1">
        <div className="w-full flex py-1 gap-x-0.5 items-center">
          {showResults && (
            <button
              className="w-9 h-9 p-1 flex-shrink-0 rounded-full flex items-center bg-gray-200"
              onClick={() => {
                const users = selectedContacts.map((contacts) => contacts.destinationId);
                setIsLoading(true);
                setContacts([]);
                setOffset(limit + offset);
                fetchSharedSpaces(limit, offset + limit, '');
                setShowResults(false);
                setSearchQuery('');
              }}
            >
              <ArrowLeftIcon className="w-full h-full" />
            </button>
          )}
        </div>
      </div>
      {selectedFilter === 'ALL' && <hr></hr>}
      {!showResults && (
        <div className="py-2 px-4 w-full flex-shrink">
          <TabsBar002
            tabOptions={shareSpaceFilter}
            activeNoBg={false}
            onTabClick={(value) => {
              setOffset(0);
              setSpaces([]);
              setCognitionList([]);
              setHasMore(true);
              setSelectedFilter(value);
            }}
            isSelected={selectedFilter}
            borderNeed={false}
          />
        </div>
      )}
      {selectedFilter === 'ALL' && (
        <div className="w-full flex relative px-4">
          <FaSearch className="h-4 w-4 text-slate-400 absolute top-1/2 -translate-y-1/2 translate-x-2" />
          <input
            placeholder="Search"
            className="w-full pr-4 pl-8 py-2 outline-none border-none bg-gray-200 rounded-full"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              debouncedSearchContacts(e.target.value);
            }}
          ></input>
        </div>
      )}
      <div className="flex-grow overflow-y-auto px-4">
        {showResults && selectedFilter === 'ALL' && (
          <div className="w-full flex flex-col bg-white rounded-md overflow-hidden ">
            {contacts.map((contact, index) => {
              return (
                <button
                  key={index}
                  className="w-full flex gap-x-2 items-center hover:bg-slate-100 px-2 py-1.5 rounded-md "
                  onClick={() => spacesCreate(contact?.destinationId, contact)}
                >
                  <img
                    key={contact?._id}
                    src={
                      contact?.displayPic
                        ? appSettings.api.S3_MEDIA_COMPRESSED + contact?.displayPic
                        : appSettings.images.Avatar
                    }
                    alt=""
                    className="w-[52px] h-[52px] md:w-[42px] md:h-[42px] flex rounded-full object-cover"
                  ></img>
                  <div className="w-[80%] flex flex-col">
                    <div className="text-start truncate font-bold">
                      {contact?.threadName || contact?.name || contact?.email}
                    </div>
                  </div>
                </button>
              );
            })}
            {contacts.length == 0 && (
              <button className="p-2 w-full hover:bg-slate-52 text-center">No Contacts Found</button>
            )}
          </div>
        )}

        {!showResults && contacts?.length === 0 && selectedFilter === 'ALL' && (
          <div className="w-full flex flex-col bg-white rounded-md overflow-hidden ">
            {spaces.map((space, index) => {
              const users = space?.users.filter((user: any) => user?.Id != authentication?.userId);

              return (
                <div
                  className="flex items-center space-x-4 p-4 hover:bg-gray-100 cursor-pointer"
                  onKeyDown={(e) => {}}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    // setShowMessage(true);
                    // setMessageUser(users);
                    // setSelectedShareId(space?._id);
                    router.push(`/thread/${space?._id}`);
                  }}
                  key={index}
                >
                  {users.length == 1 && (
                    <img
                      key={users[0]?.Id}
                      src={
                        users[0]?.DisplayPic
                          ? appSettings.api.S3_MEDIA_COMPRESSED + users[0].DisplayPic
                          : appSettings.images.Avatar
                      }
                      alt=""
                      className="w-[52px] h-[52px] md:w-[42px] md:h-[42px] flex rounded-full object-cover"
                    ></img>
                  )}
                  {users.length === 0 && (
                    <img
                      key={space?.users[0]?.Id}
                      src={
                        space?.users[0]?.DisplayPic
                          ? appSettings.api.S3_MEDIA_COMPRESSED + space?.users[0].DisplayPic
                          : appSettings.images.Avatar
                      }
                      alt=""
                      className="w-[52px] h-[52px] md:w-[42px] md:h-[42px] flex rounded-full object-cover"
                    ></img>
                  )}
                  {users.length > 1 && (
                    <div className="w-[52px] h-[52px] md:w-[42px] md:h-[42px] flex relative">
                      <img
                        key={users[0]?.Id}
                        src={
                          users[0]?.DisplayPic
                            ? appSettings.api.S3_MEDIA_COMPRESSED + users[0].DisplayPic
                            : appSettings.images.Avatar
                        }
                        alt=""
                        className="absolute top-1/2 left-0 -translate-y-1/4 w-[40px] h-[40px] z-10 md:w-[30px] md:h-[30px] object-cover rounded-full shrink-0"
                      ></img>
                      <img
                        key={users[1]?.Id}
                        src={
                          users[1]?.DisplayPic
                            ? appSettings.api.S3_MEDIA_COMPRESSED + users[1].DisplayPic
                            : appSettings.images.Avatar
                        }
                        alt=""
                        className="absolute top-0 right-0  w-[40px] h-[40px]  md:w-[30px] md:h-[30px] object-cover rounded-full shrink-0"
                      ></img>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {/* {users?.length > 0 ? generateUserString(users, 2) : generateUserString(space?.users, 1)} */}
                      {/* {chat.isGroup && '(Group)'} */}
                      {space?.threadName}
                    </p>
                    <p className="text-sm text-gray-500 truncate">{formatLastMessage(space?.lastMessage)}</p>
                  </div>
                  <div className="text-xs text-gray-400">{formatRelativeTime(space?.updatedAt)}</div>
                </div>
              );
            })}
            {spaces.length == 0 && !hasMore && (
              <button className="p-2 w-full hover:bg-slate-52 text-center">No Contacts Found</button>
            )}
          </div>
        )}

        {selectedFilter === 'BUSINESS' && (
          <div className="w-full flex flex-col bg-white rounded-md overflow-hidden ">
            {spaces.map((vendor, index) => {
              // const users = space?.users.filter((user) => user.Id != authentication?.userId);

              return (
                <>
                  {vendor?.vendor && (
                    <div
                      className="flex items-center space-x-4 p-4 hover:bg-gray-100 cursor-pointer"
                      onKeyDown={(e) => {}}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        router.push(`/thread/${vendor?._id}`);
                        // setShowMessage(true);
                        // setMessageUser(users);
                        // setSelectedShareId(vendor?.sharedSpaceId || '');
                      }}
                      key={index}
                    >
                      <img
                        key={vendor?._id}
                        src={
                          vendor?.vendor?.businessLogo
                            ? appSettings.api.S3_MEDIA_COMPRESSED + vendor?.vendor?.businessLogo
                            : appSettings.images.Avatar
                        }
                        alt=""
                        className="w-[52px] h-[52px] md:w-[42px] md:h-[42px] flex rounded-full object-cover"
                      ></img>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{vendor?.threadName}</p>
                        {/* <p className="text-xs text-gray-500 truncate">{vendor?.vendor?.businessDescription}</p> */}
                        <p className="text-sm text-gray-500 truncate">{vendor?.vendor?.businessName}</p>
                      </div>
                      <div className="text-xs text-gray-400 whitespace-nowrap">
                        {formatRelativeTime(vendor?.updatedAt)}
                      </div>
                    </div>
                  )}
                </>
              );
            })}
            {spaces.length == 0 && !isLoading && (
              <button className="p-2 w-full hover:bg-slate-52 text-center">No Business Found</button>
            )}
          </div>
        )}

        {selectedFilter === 'MY COGNITION' && (
          <div className="w-full flex flex-col bg-white rounded-md overflow-hidden ">
            {cognitionList.map((cognition, index) => {
              // const users = space?.users.filter((user) => user.Id != authentication?.userId);

              return (
                <>
                  {/* {cognition?.vendor && ( */}
                  <div
                    className="flex items-center space-x-4 p-4 hover:bg-gray-100 cursor-pointer"
                    onKeyDown={(e) => {}}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      router.push(`/thread/${cognition?._id}`);
                      // setShowMessage(true);
                      // setMessageUser(users);
                      // setSelectedShareId(vendor?.sharedSpaceId || '');
                    }}
                    key={index}
                  >
                    <img
                      key={cognition?._id}
                      src={
                        cognition?.displayImage
                          ? appSettings.api.S3_MEDIA_COMPRESSED + cognition?.displayImage
                          : appSettings.images.Avatar
                      }
                      alt=""
                      className="w-[52px] h-[52px] md:w-[42px] md:h-[42px] flex rounded-full object-cover"
                    ></img>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{cognition?.threadName}</p>
                      {/* <p className="text-xs text-gray-500 truncate">{vendor?.vendor?.businessDescription}</p> */}
                      <p className="text-sm text-gray-500 truncate">{cognition?.vendor?.businessName}</p>
                    </div>
                    <div className="text-xs text-gray-400 whitespace-nowrap">
                      {formatRelativeTime(cognition?.updatedAt)}
                    </div>
                  </div>
                </>
              );
            })}
            {cognitionList.length == 0 && !isLoading && (
              <button className="p-2 w-full hover:bg-slate-52 text-center">No Business Found</button>
            )}
          </div>
        )}

        {!isLoading &&
          hasMore &&
          (cognitionList?.length > 0 || spaces?.length > 0 || contacts?.length > 0) && (
            <div ref={moreElement} className="w-full h-[100px] flex "></div>
          )}
        {isLoading && (
          <div className="w-full h-[200px] flex items-center justify-center">
            <Loader />
          </div>
        )}

        {spaces.length == 0 && !isLoading && !searchQuery && selectedContacts.length > 0 && (
          <div className="w-full h-[30dvh] flex flex-col gap-y-2 items-center justify-center p-2">
            <span className="text-center">No shared space found. Would you like to create one.</span>
            <button className="bg-slate-800 text-white px-4 py-2 rounded-full" onClick={onSubmit}>
              Create
            </button>
          </div>
        )}
      </div>

      {/* <MessagePopup
        showMessage={showMessage}
        closeDialog={closeDialog}
        selectedSharedId={selectedSharedId}
        messageUser={messageUser}
        latestupdate={(data: SharedMessage) => {
          const updatedSpaces = [...spaces];
          const spaceIndex = updatedSpaces.findIndex((spaceList) => spaceList?._id === data?.sharedSpaceId);
          if (spaceIndex !== -1) {
            updatedSpaces[spaceIndex] = {
              ...updatedSpaces[spaceIndex],
              lastMessage: `${authentication?.profileData?.fullName || 'Unknown'}: ${
                data?.mentionMessage
                  ? data?.mentionMessage
                  : data?.contents?.length > 1
                  ? `Added ${data?.contents?.length} contents`
                  : data?.contents?.[0]?.contentType === ContentTypeEnum.TEXT
                  ? data?.contents?.[0]?.content?.text || ''
                  : data?.contents?.[0]?.contentType === ContentTypeEnum.LINK
                  ? data?.contents?.[0]?.content?.url || ''
                  : data?.contents?.[0]?.contentType === ContentTypeEnum.IMAGE
                  ? 'Added an image'
                  : 'Added a content'
              }`,
              lastActiveAt: data?.createdAt,
            };
            const [selectedSpace] = updatedSpaces.splice(spaceIndex, 1);
            updatedSpaces.unshift(selectedSpace);
            setSpaces(updatedSpaces);
          }
        }}
        forwardingData={(data: any) => {
          const forwardedIds = data?.ids;
          const updatedSpaces = [...spaces];

          forwardedIds.forEach((id: string, index: number) => {
            const spaceIndex = updatedSpaces.findIndex((space) => space._id === id);

            if (spaceIndex !== -1) {
              const [selectedSpace] = updatedSpaces.splice(spaceIndex, 1);

              selectedSpace.lastMessage = `${authentication?.profileData?.fullName || 'Unknown'}: ${
                data?.message?.mentionMessage
                  ? data?.message?.mentionMessage
                  : data?.message?.contents?.length > 1
                  ? `Added ${data?.message?.contents?.length} contents`
                  : data?.message?.contents?.[0]?.contentType === ContentTypeEnum.TEXT
                  ? data?.message?.contents?.[0]?.content?.text || ''
                  : data?.message?.contents?.[0]?.contentType === ContentTypeEnum.LINK
                  ? data?.message?.contents?.[0]?.content?.url || ''
                  : data?.message?.contents?.[0]?.contentType === ContentTypeEnum.IMAGE
                  ? 'Added an image'
                  : 'Added a content'
              }`;
              selectedSpace.lastActiveAt = new Date().toISOString();

              updatedSpaces.splice(index, 0, selectedSpace);
            } else {
              getSharedSpaceById(id)
                .then((res) => {
                  setSpaces([
                    {
                      ...res.data.sharedSpace,
                      lastMessage: `${authentication?.profileData?.fullName || 'Unknown'}: ${
                        data?.message?.mentionMessage
                          ? data?.message?.mentionMessage
                          : data?.message?.contents?.length > 1
                          ? `Added ${data?.message?.contents?.length} contents`
                          : data?.message?.contents?.[0]?.contentType === ContentTypeEnum.TEXT
                          ? data?.message?.contents?.[0]?.content?.text || ''
                          : data?.message?.contents?.[0]?.contentType === ContentTypeEnum.LINK
                          ? data?.message?.contents?.[0]?.content?.url || ''
                          : data?.message?.contents?.[0]?.contentType === ContentTypeEnum.IMAGE
                          ? 'Added an image'
                          : 'Added a content'
                      }`,
                      lastActiveAt: new Date().toISOString(),
                    },
                    ...spaces,
                  ]);
                })
                .catch((error) => {});
            }
          });

          setSpaces(updatedSpaces);
        }}
        newShareIdReplay={(data: SharedMessage) => {
          if (selectedFilter === 'Personals') {
            setSelectedFilter('ALL');
          }
          const formattedMentions = data?.mentions.map((mention) => ({
            Id: mention?.userId,
            Name: mention?.Name,
            Email: mention?.Email,
            Handle: mention?.Handle,
            DisplayPic: mention?.DisplayPic,
          }));

          const updatedSpaces = [...spaces];

          setMessageUser([...messageUser, ...formattedMentions]);
          const spaceIndex = updatedSpaces.findIndex((space) => space._id === selectedSharedId);
          if (spaceIndex !== -1) {
            const [selectedSpace] = updatedSpaces.splice(spaceIndex, 1);

            selectedSpace.lastMessage = `${authentication?.profileData?.fullName || 'Unknown'}: ${
              data?.mentionMessage
                ? data?.mentionMessage
                : data?.contents?.length > 1
                ? `Added ${data?.contents?.length} contents`
                : data?.contents?.[0]?.contentType === ContentTypeEnum.TEXT
                ? data?.contents?.[0]?.content?.text || ''
                : data?.contents?.[0]?.contentType === ContentTypeEnum.LINK
                ? data?.contents?.[0]?.content?.url || ''
                : data?.contents?.[0]?.contentType === ContentTypeEnum.IMAGE
                ? 'Added an image'
                : 'Added a content'
            }`;
            selectedSpace.lastActiveAt = new Date().toISOString();
            selectedSpace.users = [...messageUser, ...formattedMentions];
            selectedSpace._id = data?.sharedSpaceId;
            selectedSpace.lastActiveAt = new Date().toISOString();

            updatedSpaces.unshift(selectedSpace);
          } else {
            getSharedSpaceById(data?.sharedSpaceId)
              .then((res) => {
                setSpaces([
                  {
                    ...res.data.sharedSpace,
                    lastMessage: `${authentication?.profileData?.fullName || 'Unknown'}: ${
                      data?.mentionMessage
                        ? data?.mentionMessage
                        : data?.contents?.length > 1
                        ? `Added ${data?.contents?.length} contents`
                        : data?.contents?.[0]?.contentType === ContentTypeEnum.TEXT
                        ? data?.contents?.[0]?.content?.text || ''
                        : data?.contents?.[0]?.contentType === ContentTypeEnum.LINK
                        ? data?.contents?.[0]?.content?.url || ''
                        : data?.contents?.[0]?.contentType === ContentTypeEnum.IMAGE
                        ? 'Added an image'
                        : 'Added a content'
                    }`,
                    lastActiveAt: new Date().toISOString(),
                  },
                  ...spaces,
                ]);
              })
              .catch((error) => {});
          }
          setSpaces(updatedSpaces);
          setSelectedShareId(data?.sharedSpaceId);
        }}
      /> */}
    </>
  );
};

export default MessageListing;
